/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { PanState } from '../diagram.types';

import { Ref, useRef } from 'preact/hooks';
import { useDrag } from './useDrag';
import { PanBounds, pan } from '../utils/panUtils';
import { centerXYToPanXY } from '../utils/diagramUtils';
/**
 * Options for useDragPan hook
 */
type UseDragPanOptions = {
  panProps: {
    panning: 'centerContent' | 'fixed' | 'off';
    centerX: number;
    centerY: number;
    panDirection: 'x' | 'y' | 'any';
    onPan?: (detail: PanState) => void;
  };
  captureTargetRef: Ref<HTMLDivElement>;
  panBounds?: PanBounds;
  onDragPanStart?: () => void;
  onDragPanEnd?: () => void;
  zoom: number;
  width: number;
  height: number;
};

const useDragPan = ({
  panProps,
  captureTargetRef,
  panBounds,
  width,
  height,
  zoom,
  onDragPanStart,
  onDragPanEnd
}: UseDragPanOptions) => {
  const prevPanState = useRef({ panX: 0, panY: 0 });
  const initialPanState = useRef({ panX: 0, panY: 0 });
  const dragProps = useDrag({
    captureTargetRef,
    onDragStart: () => {
      const panValues = centerXYToPanXY(panProps.centerX, panProps.centerY, zoom, width, height);
      initialPanState.current = {
        panX: panValues.panX,
        panY: panValues.panY
      };
      onDragPanStart && onDragPanStart();
    },
    onDragMove: ({ dx, dy }) => {
      const nextPan = {
        panX: initialPanState.current.panX + dx,
        panY: initialPanState.current.panY + dy
      };
      const { newPan } = pan(
        prevPanState.current,
        nextPan,
        panProps.panDirection,
        zoom,
        width,
        height,
        panBounds,
        panProps.onPan
      );
      prevPanState.current = newPan;
    },
    onDragEnd: ({ originalEvent }) => {
      // If panning occurred, call preventDefault to prevent selection from occurring
      if (
        initialPanState.current.panX !== prevPanState.current.panX ||
        initialPanState.current.panY !== prevPanState.current.panY
      )
        originalEvent.preventDefault();
      onDragPanEnd && onDragPanEnd();
    },
    isDisabled: panProps.panning === 'off'
  });

  return dragProps;
};

export { useDragPan };
